/* eslint-disable import/no-named-as-default-member */
/* eslint-disable @typescript-eslint/no-explicit-any */
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { uniqBy } from 'lodash'
import { useState, useEffect } from 'react'

import { ErrorProps } from './common'
import useNextToken from './useNextToken'
import { Event as EventApi, FormEvent as FormEventApi } from '../API'
import {
  listEventsForDownload,
  listFormEventsForDownload,
} from '../api/queries'
import { DEFAULT_ENTRIES_PER_PAGE } from '../constants/api'
import { fetchQuery } from '../utils/ApiUtils'

dayjs.extend(utc)
dayjs.extend(timezone)

type Event = Omit<Exclude<EventApi, null>, '__typename'>
type FormEvent = Omit<Exclude<FormEventApi, null>, '__typename'>

export const getEventForDownload = async (eventId: string) => {
  try {
    const { items } = (await fetchQuery('listEvents', listEventsForDownload, {
      limit: DEFAULT_ENTRIES_PER_PAGE,
      filter: {
        id: { eq: eventId },
      },
    })) as unknown as { items: Event[]; nextToken: any }
    return items.length > 0 ? items[0] : null
  } catch (e) {
    console.log(e)
  }
  return null
}

export const useEventsForDownload = () => {
  const [events, setEvents] = useState<Array<Event | FormEvent>>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<ErrorProps | null>()

  const { setNewToken, startUsingToken, onTokenSuccess } = useNextToken()

  const queryInfo = [
    {
      options: {
        filter: {
          endDateTime: {
            gt: dayjs().tz('Asia/Manila'),
          },
        },
      },
      name: 'listEvents',
      query: listEventsForDownload,
      isFP: false,
    },
    {
      options: {
        filter: {
          registrationCutOff: {
            gt: dayjs().tz('Asia/Manila').subtract(6, 'M'),
          },
        },
      },
      name: 'listFormEvents',
      query: listFormEventsForDownload,
      isFP: true,
    },
  ]

  useEffect(() => {
    let didCancel = false

    let allItems = []
    let currentNextToken = null

    let queryCount = 0

    const fetchData = async () => {
      if (didCancel) return
      if (!currentNextToken && queryCount === queryInfo.length) {
        setLoading(false)
        return
      }
      if (queryCount < queryInfo.length) {
        setLoading(true)

        if (currentNextToken) startUsingToken(currentNextToken)

        try {
          const { items, nextToken: newNextToken } = (await fetchQuery(
            queryInfo[queryCount].name,
            queryInfo[queryCount].query,
            {
              limit: DEFAULT_ENTRIES_PER_PAGE,
              ...(currentNextToken && { nextToken: currentNextToken }),
              ...queryInfo[queryCount].options,
            },
          )) as unknown as { items: Event[] | FormEvent[]; nextToken: any }

          if (queryInfo[queryCount].isFP) {
            const withFP = items.reduce((prev, cur) => {
              return prev.concat({
                ...cur,
                isFP: true,
              })
            }, [])
            allItems = uniqBy([...allItems, ...withFP], 'id')
          } else allItems = uniqBy([...allItems, ...items], 'id')

          if (currentNextToken) onTokenSuccess(currentNextToken)
          setEvents(allItems)

          currentNextToken = newNextToken
          if (newNextToken) {
            setNewToken(newNextToken)
            fetchData()
          } else {
            queryCount++
            setNewToken(null)
            fetchData()
          }
        } catch (e) {
          console.log(e)
          setError({
            title: 'Fail to load events',
            message: '',
          })
        }
      }
    }

    fetchData()

    return () => {
      didCancel = true
    }
  }, [setNewToken])

  return {
    events,
    apiLoading: loading,
    apiError: error,
  }
}
